import { getSessionToken } from './sessionTokenService';

interface PublicListTickerMapping {
  id: number;
  ticker_symbol: string;
  ticker_name: string;
  order_num: number;
}

interface PublicListPreferences {
  id: number;
  daily_indicators_to_hide: string[] | null;
  insider_indicators_to_hide?: string[] | null;
  pros_cons_indicators_to_hide?: string[] | null;
  performance_indicators_to_hide: string[] | null;
  technical_indicators_to_hide: string[] | null;
  fundamental_indicators_to_hide: string[] | null;
  analyst_indicators_to_hide: string[] | null;
}

interface GetPublicListResponse {
  success: boolean;
  message: string;
  data: {
    public_list_name: string | null;
    list_preferences: PublicListPreferences | null;
    ticker_mappings: PublicListTickerMapping[];
    owner_has_pro_subscription: boolean;
  };
}

class PublicListApiService {
  private baseUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/v1/public-list`;

  async getPublicList(publicUuid: string): Promise<GetPublicListResponse> {
    // Add session token to all requests
    const sessionToken = getSessionToken();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (sessionToken) {
      headers['X-Session-Token'] = sessionToken;
    }

    const response = await fetch(`${this.baseUrl}/${publicUuid}`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
      // For 404 errors, throw a specific error that won't trigger retries
      if (response.status === 404) {
        const error = new Error('Public list not found');
        (error as any).status = 404;
        throw error;
      }
      // For 403 errors (share_enabled is false), treat similarly to 404
      if (response.status === 403) {
        const error = new Error('This list is not publicly shared');
        (error as any).status = 403;
        throw error;
      }
      throw new Error(errorData.message || `HTTP ${response.status}`);
    }

    return response.json();
  }
}

export const publicListApi = new PublicListApiService();
export type { 
  GetPublicListResponse, 
  PublicListTickerMapping, 
  PublicListPreferences 
};
